// client/src/utils/apiInterceptor.js
import { api } from './api';
import { useEffect } from 'react';
import { useAuth } from '@/components/hooks/useAuth';
import { getLogger } from "@/utils/logger";
import { getRequestId } from "@/utils/requestId";

const log = getLogger("apiInterceptor");

// Module-level singleton — survives re-renders and interceptor re-registration.
// Guards against concurrent 401s each trying to refresh the token independently.
let isRefreshing = false;
let refreshQueue = [];

function processRefreshQueue(token, error) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
}

/**
 * Hook to set up axios interceptors that automatically add the access token
 * to all outgoing requests to the API, and retry requests that receive a 401
 * by refreshing the token exactly once (queuing any concurrent 401 retries).
 */
export function useApiAuthInterceptor() {
  const { getTokenOrThrow, isAuthenticated } = useAuth();

  useEffect(() => {
    // Set up request interceptor to add token
    const requestInterceptorId = api.interceptors.request.use(
      async (config) => {
        const requestId = config.headers?.["x-request-id"] || getRequestId();
        config.headers = config.headers || {};
        config.headers["x-request-id"] = requestId;
        const existingAuthorization =
          config.headers.Authorization || config.headers.authorization;
        if (existingAuthorization) {
          return config;
        }
        // Only add the token if the identity is authenticated
        if (isAuthenticated) {
          try {
            const token = await getTokenOrThrow();
            if (token) {
              // Add the token to the request headers
              config.headers.Authorization = `Bearer ${token}`;
              log.info(
                `[apiInterceptor:useApiAuthInterceptor] > [Request]: addAuthToken > [Response]: added > [Comment]: url=${config.url}`,
                { requestId, url: config.url }
              );
            }
          } catch (error) {
            log.error(
              `[apiInterceptor:useApiAuthInterceptor] > [Request]: getTokenOrThrow > [Response]: error=${error?.message || "unknown"} > [Comment]: url=${config?.url || "unknown"}`,
              { requestId, errorMessage: error?.message, url: config?.url }
            );
            // Let the request proceed without the token
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Set up response interceptor to handle 401 token-expiry errors.
    // When the server returns 401, attempt a single token refresh and replay
    // the original request.  Any additional 401s that arrive while a refresh
    // is already in flight are queued and replayed with the same new token,
    // preventing a thundering-herd of simultaneous refresh calls.
    const responseInterceptorId = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalConfig = error.config;

        // Only handle 401s; skip if this request has already been retried
        // once (to prevent infinite retry loops).
        if (
          !originalConfig ||
          error.response?.status !== 401 ||
          originalConfig._retry
        ) {
          return Promise.reject(error);
        }

        if (!isAuthenticated) {
          log.warn(
            `[apiInterceptor:responseInterceptor] > [Request]: 401 > [Response]: skipped > [Comment]: identity not authenticated, url=${originalConfig?.url || "unknown"}`
          );
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // A refresh is already underway — queue this request so it is
          // replayed with the new token once the refresh completes.
          return new Promise((resolve, reject) => {
            refreshQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalConfig.headers = originalConfig.headers || {};
              originalConfig.headers.Authorization = `Bearer ${token}`;
              return api(originalConfig);
            })
            .catch((err) => Promise.reject(err));
        }

        // Mark as retried and start the token refresh.
        originalConfig._retry = true;
        isRefreshing = true;

        try {
          const token = await getTokenOrThrow({ ignoreCache: true });
          log.info(
            `[apiInterceptor:responseInterceptor] > [Request]: tokenRefresh > [Response]: success > [Comment]: url=${originalConfig?.url || "unknown"}`
          );
          processRefreshQueue(token, null);
          originalConfig.headers = originalConfig.headers || {};
          originalConfig.headers.Authorization = `Bearer ${token}`;
          return api(originalConfig);
        } catch (refreshError) {
          log.error(
            `[apiInterceptor:responseInterceptor] > [Request]: tokenRefresh > [Response]: error=${refreshError?.message || "unknown"} > [Comment]: url=${originalConfig?.url || "unknown"}`
          );
          processRefreshQueue(null, refreshError);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    );

    // Clean up both interceptors when the component unmounts or deps change
    return () => {
      api.interceptors.request.eject(requestInterceptorId);
      api.interceptors.response.eject(responseInterceptorId);
    };
  }, [getTokenOrThrow, isAuthenticated]);

  // No need to return anything, the interceptors work automatically
}

// Usage example:
// 1. Import this hook in your App.jsx component
// 2. Call it at the top level to set up the interceptors for all api calls
// function App() {
//   useApiAuthInterceptor();
//   return (/* your app components */);
// }
